import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { api } from '../services/api'

// Permission Context
const PermissionContext = createContext()

// Initial state
const initialState = {
  permissions: {},
  userRole: null,
  isLoading: true,
  error: null,
  lastFetched: null,
}

// Permission actions
const PERMISSION_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_PERMISSIONS: 'CLEAR_PERMISSIONS',
}

// Permission reducer
const permissionReducer = (state, action) => {
  switch (action.type) {
    case PERMISSION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }

    case PERMISSION_ACTIONS.SET_PERMISSIONS:
      return {
        ...state,
        permissions: action.payload.permissions,
        userRole: action.payload.userRole,
        isLoading: false,
        error: null,
        lastFetched: new Date().getTime(),
      }

    case PERMISSION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }

    case PERMISSION_ACTIONS.CLEAR_PERMISSIONS:
      return {
        ...initialState,
        isLoading: false,
      }

    default:
      return state
  }
}

// Permission Provider Component
export const PermissionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(permissionReducer, initialState)
  const { isAuthenticated, user, isWorkspaceSwitched, ownerAccountId } = useAuth()

  // Fetch permissions when auth state changes or workspace switches
  useEffect(() => {
    if (!isAuthenticated || !user) {
      dispatch({ type: PERMISSION_ACTIONS.CLEAR_PERMISSIONS })
      return
    }

    fetchPermissions()
  }, [isAuthenticated, user, isWorkspaceSwitched, ownerAccountId])

  const fetchPermissions = async () => {
    try {
      dispatch({ type: PERMISSION_ACTIONS.SET_LOADING, payload: true })

      // Build API params
      const params = {}
      if (isWorkspaceSwitched && ownerAccountId) {
        params.owner_account_id = ownerAccountId
      }

      const response = await api.get('/check-permissions/', { params })

      if (response.data.type === 'success') {
        dispatch({
          type: PERMISSION_ACTIONS.SET_PERMISSIONS,
          payload: {
            permissions: response.data.data.permissions,
            userRole: response.data.data.user_role,
          }
        })
      } else {
        throw new Error(response.data.message || 'Failed to fetch permissions')
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      dispatch({
        type: PERMISSION_ACTIONS.SET_ERROR,
        payload: error.message || 'Failed to fetch permissions'
      })
    }
  }

  // Refresh permissions manually
  const refreshPermissions = () => {
    fetchPermissions()
  }

  const value = {
    ...state,
    refreshPermissions,
  }

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  )
}

// Custom hook to use permissions
export const usePermissions = () => {
  const context = useContext(PermissionContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider')
  }
  return context
}

// Helper hook for checking specific permissions
export const usePermissionCheck = () => {
  const { permissions, userRole } = usePermissions()

  const hasPermission = (resource, action) => {
    if (!permissions[resource]) {
      return false
    }
    return permissions[resource].includes(action)
  }

  const canCreate = (resource) => hasPermission(resource, 'create')
  const canRead = (resource) => hasPermission(resource, 'read')
  const canUpdate = (resource) => hasPermission(resource, 'update')
  const canDelete = (resource) => hasPermission(resource, 'delete')
  const canList = (resource) => hasPermission(resource, 'list')

  // Specific permission checks
  const canAssignCase = () => hasPermission('case', 'assign')
  const canUnassignCase = () => hasPermission('case', 'unassign')
  const canManageRoles = () => hasPermission('case', 'manage_roles')

  // Role-based checks
  const isOwner = () => userRole === 'owner'
  const isAdmin = () => userRole === 'admin'
  const isDiamond = () => userRole === 'diamond'
  const isGold = () => userRole === 'gold'
  const isSilver = () => userRole === 'silver'

  // Helper to check multiple permissions
  const hasAnyPermission = (resource, actions) => {
    return actions.some(action => hasPermission(resource, action))
  }

  const hasAllPermissions = (resource, actions) => {
    return actions.every(action => hasPermission(resource, action))
  }

  return {
    hasPermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canList,
    canAssignCase,
    canUnassignCase,
    canManageRoles,
    isOwner,
    isAdmin,
    isDiamond,
    isGold,
    isSilver,
    hasAnyPermission,
    hasAllPermissions,
    userRole,
    permissions,
  }
}